import './rvChart.css';

// Shared look for the restyled Firm Dashboard charts (the ones whose root carries the `rv-chart` class - see rvChart.css).
// Everything here is presentation for chart.js / react-gauge-component options: colours, bar shape, grid lines, tooltip and animation.
// The data, scales' ranges, callbacks and click handlers stay in each chart.

export const RV_CHART_CLASS = 'rv-chart';

export const RV_CHART_COLORS = {
    primary: '#1667c7',
    primarySoft: '#b9d4fb',
    ink: '#1f2433',
    muted: '#667085',
    faint: '#98a2b3',
    grid: '#eef0f6',
    red: '#f6414d',
    amber: '#f9a300',
    green: '#87bb00',
    needle: '#2f4f4f',
};

// The four options of the "vs Budget" gauges (the same labels the period buttons show) - written under the chart's title.
// Slices of a doughnut / pie: the dashboard's colours first, then softer ones for the long tail of categories.
export const RV_CHART_CATEGORY_COLORS = [
    RV_CHART_COLORS.primary,
    RV_CHART_COLORS.amber,
    RV_CHART_COLORS.green,
    RV_CHART_COLORS.red,
    '#8b5cf6',
    '#06b6d4',
    '#f472b6',
    '#facc15',
    '#14b8a6',
    RV_CHART_COLORS.faint,
];

export const GAUGE_PERIOD_LABELS: Record<string, string> = {
    MONTH: 'This Month',
    PREVIOUS_MONTH: 'Previous Month',
    FINANCIAL_YEAR: 'Financial Year',
    PREV_FINANCIAL_YEAR: 'Previous Financial Year',
};

// Colour of the "progress to budget" bar - the same three zones the gauge below it draws (red up to a third of the budget,
// amber up to two thirds, green beyond), so the bar and the gauge always agree.
export const getBudgetProgressColor = (percentage: number): string => {
    if (percentage < 100 / 3) {
        return RV_CHART_COLORS.red;
    }
    if (percentage < 200 / 3) {
        return RV_CHART_COLORS.amber;
    }
    return RV_CHART_COLORS.green;
};

// The same four labels for charts whose period option is a number (0 to 3) instead of a name.
export const BUDGET_PERIOD_LABELS_BY_INDEX = ['This Month', 'Previous Month', 'Financial Year', 'Previous Financial Year'];

const prefersReducedMotion = (): boolean =>
    typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// True when there is nothing to draw: no values at all, or every value is zero / missing.
export const isChartDataEmpty = (...series: Array<Array<number | null | undefined> | null | undefined>): boolean =>
    series.every((values) => !values || values.every((value) => !value));

// Rounded bars with no outline. Pass one colour, or one per bar. Stacked segments get a small radius and a thin white
// separator instead, so the pieces of one bar read as pieces.
export const rvBarDatasetStyle = (color: string | string[], isStacked: boolean = false) => ({
    backgroundColor: color,
    borderColor: isStacked ? '#ffffff' : color,
    borderWidth: isStacked ? 1 : 0,
    borderRadius: isStacked ? 3 : 5,
    borderSkipped: false as const,
    hoverBackgroundColor: color,
    hoverBorderColor: color,
    maxBarThickness: 24,
});

// The bars grow in one after another whenever the data changes (period switch, refresh).
export const rvChartAnimation = () => {
    if (prefersReducedMotion()) {
        return { duration: 0 };
    }
    return {
        duration: 700,
        easing: 'easeOutQuart' as const,
        // tslint:disable-next-line: no-any
        delay: (context: any) => (context.type === 'data' && context.mode === 'default' ? context.dataIndex * 35 : 0),
    };
};

// Light value grid, no axis line; the category axis has no grid at all.
export const rvValueAxisStyle = {
    grid: { color: RV_CHART_COLORS.grid, drawBorder: false },
    ticks: { color: RV_CHART_COLORS.muted, font: { size: 11 } },
};
export const rvCategoryAxisStyle = {
    grid: { display: false, drawBorder: false },
    ticks: { color: RV_CHART_COLORS.ink, font: { size: 12 } },
};

export const rvTooltipStyle = {
    backgroundColor: RV_CHART_COLORS.ink,
    padding: 10,
    cornerRadius: 8,
    boxPadding: 4,
    titleFont: { weight: 'bold' as const },
};

export const rvLegendLabels = {
    usePointStyle: true,
    pointStyle: 'rectRounded' as const,
    boxWidth: 10,
    boxHeight: 10,
    color: RV_CHART_COLORS.muted,
    font: { size: 12 },
};

// react-gauge-component: thinner rounded arc, softer tick labels, and a pointer that sweeps to the new value.
export const rvGaugeArcStyle = {
    width: 0.11,
    padding: 0.008,
    cornerRadius: 6,
};

export const rvGaugePointerStyle = {
    color: RV_CHART_COLORS.needle,
    length: 0.78,
    width: 9,
    animate: true,
    animationDuration: 1000,
    animationDelay: 0,
};

export const rvGaugeTickLabelStyle = {
    fill: RV_CHART_COLORS.muted,
    fontSize: '12px',
};

export const rvGaugeValueLabelStyle = {
    fontSize: '26px',
    fontWeight: 800,
    fill: RV_CHART_COLORS.ink,
    textShadow: 'none',
};
